import { useEffect, useMemo, useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView, Image, StyleSheet, useWindowDimensions } from 'react-native'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { supabase } from '../../lib/supabase'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'

const CATEGORY_COLORS = {
  'Risultati':     '#10B981',
  'Trasferimenti': '#3B82F6',
  'Generale':      '#8B5CF6',
  'Preview':       '#EF4444',
  'Live':          '#ef44d2',
  'Altro':         '#F59E0B',
}

function NewsBody({ html, textColor }) {
  const { width } = useWindowDimensions()
  try {
    const RenderHtml = require('react-native-render-html').default
    const tagsStyles = {
      body: { color: textColor, fontSize: 14, lineHeight: 25 },
      p:    { marginBottom: 12, color: textColor },
      h2:   { fontSize: 20, fontWeight: '800', marginTop: 20, marginBottom: 10, color: textColor },
      h3:   { fontSize: 17, fontWeight: '700', marginTop: 16, marginBottom: 8, color: textColor },
      a:    { color: tc.rosaText },
      img:  { borderRadius: 10, marginVertical: 16 },
      ul:   { color: textColor },
      ol:   { color: textColor },
      li:   { color: textColor, marginBottom: 4 },
    }
    return (
      <RenderHtml
        contentWidth={width - 40}
        source={{ html: html ?? '' }}
        tagsStyles={tagsStyles}
        enableExperimentalBRCollapsing
      />
    )
  } catch {
    const plain = (html ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
    return (
      <Text style={{ fontSize: 14, color: textColor, lineHeight: 22 }}>{plain}</Text>
    )
  }
}

export default function NewsDetail() {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const router = useRouter()
  const { id } = useLocalSearchParams()
  const insets = useSafeAreaInsets()
  const styles = useMemo(() => makeStyles(tc), [tc])

  const [news, setNews]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!id) return
    supabase
      .from('dim_news')
      .select('id, title, excerpt, body, category, published_at, image_url, is_pinned')
      .eq('id', id)
      .eq('is_published', true)
      .single()
      .then(({ data, error }) => {
        if (error || !data) setNotFound(true)
        else setNews(data)
        setLoading(false)
      })
  }, [id])

  return (
    <View style={[{ flex: 1 }, { backgroundColor: tc.bg }]}>
      {/* Header custom */}
      <View style={[styles.header, { paddingTop: insets.top + 8, backgroundColor: tc.bg }]}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <Text style={styles.backArrow}>‹</Text>
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: tc.text }]}>Articolo</Text>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 40 }}
      >
        {loading && <Text style={[styles.center, { color: tc.textMuted }]}>{t('news.loading')}</Text>}
        {notFound && <Text style={[styles.center, { color: tc.textMuted }]}>{t('news.not_found')}</Text>}

        {news && (
          <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
            {news.image_url && (
              <Image source={{ uri: news.image_url }} style={styles.coverImage} resizeMode="cover" />
            )}
            <View style={styles.cardBody}>
              <View style={styles.metaRow}>
                {news.is_pinned && (
                  <View style={styles.pinnedBadge}>
                    <Text style={styles.pinnedBadgeText}>{t('news.pinned')}</Text>
                  </View>
                )}
                <View style={[styles.catBadge, { backgroundColor: CATEGORY_COLORS[news.category] ?? '#8B5CF6' }]}>
                  <Text style={styles.catBadgeText}>{news.category}</Text>
                </View>
                <Text style={[styles.dateText, { color: tc.textMuted }]}>
                  {news.published_at ? new Date(news.published_at).toLocaleDateString('it-IT', {
                    day: '2-digit', month: 'long', year: 'numeric'
                  }) : ''}
                </Text>
              </View>
              <Text style={[styles.title, { color: tc.text }]}>{news.title}</Text>
              {news.excerpt && (
                <View style={styles.excerptWrap}>
                  <Text style={[styles.excerpt, { color: tc.textMuted }]}>{news.excerpt}</Text>
                </View>
              )}
              {news.body && (
                <NewsBody html={news.body} textColor={tc.text} />
              )}
            </View>
          </View>
        )}
      </ScrollView>
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  header:          { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: tc.border },
  backBtn:         { padding: 4 },
  backArrow:       { color: tc.rosaText, fontSize: 36, lineHeight: 36 },
  headerTitle:     { fontFamily: fonts.condensedBold, fontSize: 16 },
  center:          { textAlign: 'center', padding: 40, fontSize: 13 },
  card:            { borderRadius: 16, borderWidth: 1, overflow: 'hidden', marginHorizontal: 16, marginTop: 16 },
  coverImage:      { width: '100%', height: 280 },
  cardBody:        { padding: 24, paddingHorizontal: 20 },
  metaRow:         { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' },
  pinnedBadge:     { backgroundColor: tc.rosaBorder, borderRadius: 20, paddingHorizontal: 7, paddingVertical: 2 },
  pinnedBadgeText: { fontSize: 10, color: tc.rosaText, fontFamily: fonts.condensedBold, letterSpacing: 0.5, textTransform: 'uppercase' },
  catBadge:        { borderRadius: 20, paddingHorizontal: 8, paddingVertical: 3 },
  catBadgeText:    { color: tc.brandText, fontSize: 10, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.5 },
  dateText:        { fontSize: 11 },
  title:           { fontSize: 22, fontWeight: '800', lineHeight: 28, marginBottom: 12 },
  excerptWrap:     { borderLeftWidth: 3, borderLeftColor: tc.rosa, paddingLeft: 12, marginBottom: 20 },
  excerpt:         { fontSize: 15, lineHeight: 24, fontStyle: 'italic' },
})